/**
 * Profile Completion Ring
 * 
 * Visual progress indicator for profile completion.
 * Shows tier progress and next unlock milestone.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Star, Heart, Users, Crown, Check, ChevronRight } from 'lucide-react';

// Completion tier configuration
const TIER_CONFIG = {
    incomplete: {
        label: 'New Explorer',
        icon: Star,
        color: '#6B7280', // gray-500
        bgClass: 'bg-gray-800',
        next: 'basic',
        requirement: 'Rate 10 items to unlock filters'
    },
    basic: {
        label: 'Basic Explorer',
        icon: Heart,
        color: '#60A5FA', // blue-400
        bgClass: 'bg-blue-900/30',
        next: 'standard',
        requirement: 'Add bio and rate 50 items for social features'
    },
    standard: {
        label: 'Standard',
        icon: Users,
        color: '#4ADE80', // green-400
        bgClass: 'bg-green-900/30',
        next: 'complete',
        requirement: 'Rate 100 items and set role preference'
    },
    complete: {
        label: 'Complete',
        icon: Crown,
        color: '#C084FC', // purple-400
        bgClass: 'bg-purple-900/30',
        next: 'detailed',
        requirement: 'Set safe word and aftercare preferences'
    },
    detailed: {
        label: 'Detailed',
        icon: Crown,
        color: '#FBBF24', // amber-400
        bgClass: 'bg-amber-900/30',
        next: null,
        requirement: 'Profile complete!'
    }
};

const TIER_ORDER = ['incomplete', 'basic', 'standard', 'complete', 'detailed'];

function ProfileCompletionRing({
    tier = 'incomplete',
    ratingCount = 0,
    hasBio = false,
    hasRole = false,
    hasSafeWord = false,
    size = 'md', // 'sm', 'md', 'lg'
    showDetails = true
}) {
    const config = TIER_CONFIG[tier] || TIER_CONFIG.incomplete;
    const tierIndex = TIER_ORDER.indexOf(tier);
    const progress = ((tierIndex + 1) / TIER_ORDER.length) * 100;

    // Calculate next milestone
    const nextMilestone = {
        ratings: tierIndex < 1 ? 10 : tierIndex < 2 ? 50 : tierIndex < 3 ? 100 : null,
        bio: tierIndex < 2,
        role: tierIndex < 3,
        safeWord: tierIndex < 4
    };

    const sizeConfig = {
        sm: { ring: 48, stroke: 4, icon: 16, text: 'text-xs' },
        md: { ring: 80, stroke: 6, icon: 24, text: 'text-sm' },
        lg: { ring: 120, stroke: 8, icon: 32, text: 'text-base' }
    };

    const s = sizeConfig[size];
    const radius = (s.ring - s.stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    const Icon = config.icon;

    return (
        <div className="flex flex-col items-center">
            {/* Ring */}
            <div className="relative" style={{ width: s.ring, height: s.ring }}>
                <svg className="transform -rotate-90" width={s.ring} height={s.ring}>
                    {/* Background track */}
                    <circle
                        cx={s.ring / 2}
                        cy={s.ring / 2}
                        r={radius}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={s.stroke}
                        className="text-dark-700"
                    />
                    {/* Progress arc */}
                    <motion.circle
                        cx={s.ring / 2}
                        cy={s.ring / 2}
                        r={radius}
                        fill="none"
                        stroke={config.color}
                        strokeWidth={s.stroke}
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        initial={{ strokeDashoffset: circumference }}
                        animate={{ strokeDashoffset }}
                        transition={{ duration: 1, ease: "easeOut" }}
                    />
                </svg>

                {/* Center icon */}
                <div
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ color: config.color }}
                >
                    <Icon size={s.icon} />
                </div>
            </div>

            {/* Tier label */}
            <div className={`mt-2 font-bold ${s.text}`} style={{ color: config.color }}>
                {config.label}
            </div>

            {/* Details panel */}
            {showDetails && (
                <div className="mt-4 w-full max-w-xs space-y-3">
                    {/* Progress stats */}
                    <div className="flex justify-around text-center text-xs">
                        <div>
                            <div className="text-lg font-bold text-cream">{ratingCount}</div>
                            <div className="text-dark-400">Ratings</div>
                        </div>
                        <div>
                            <div className={`text-lg font-bold ${hasBio ? 'text-green-400' : 'text-dark-500'}`}>
                                {hasBio ? <Check size={20} className="mx-auto" /> : '—'}
                            </div>
                            <div className="text-dark-400">Bio</div>
                        </div>
                        <div>
                            <div className={`text-lg font-bold ${hasRole ? 'text-green-400' : 'text-dark-500'}`}>
                                {hasRole ? <Check size={20} className="mx-auto" /> : '—'}
                            </div>
                            <div className="text-dark-400">Role</div>
                        </div>
                        <div>
                            <div className={`text-lg font-bold ${hasSafeWord ? 'text-green-400' : 'text-dark-500'}`}>
                                {hasSafeWord ? <Check size={20} className="mx-auto" /> : '—'}
                            </div>
                            <div className="text-dark-400">Safe Word</div>
                        </div>
                    </div>

                    {/* Next milestone */}
                    {config.next && (
                        <div className="p-3 bg-dark-800/50 border border-dark-700/50 text-xs text-dark-300">
                            <div className="flex items-center gap-2">
                                <ChevronRight size={14} className="text-wine-400" />
                                <span>{config.requirement}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default ProfileCompletionRing;
